import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import SpinnerCargando from "../SpinnerCargando";
import instanciaAxios from "../../../miscelaneo/instanciaAxios";
import { FaSearch } from "react-icons/fa";

import FuncionesGenerales from "../FuncionesGenerales";

const DEFAULT_ORIGEN = [1, 2, 3];

const TablaProducto = ({
  setProductoSeleccionado,
  descripcion,
  tipo_producto,
  tipo_tabla = 'normal',
  id_sucursal = 0,
  origen = DEFAULT_ORIGEN,
  incluirInactivos = false,
}) => {
  const {formatoMoneda} = FuncionesGenerales()
  const [filtro, setFiltro] = useState("");
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchApplied, setSearchApplied] = useState("");

  // Serialize array props to stable strings for useCallback deps
  const tipoProductoKey = JSON.stringify(tipo_producto);
  const origenKey = JSON.stringify(origen);

  const fetchProductos = useCallback(async (page, limit, search) => {
    try {
      setCargando(true);
      let respuesta = {};

      if (tipo_tabla === 'inventarioPorSucursal') {
        if (id_sucursal == 0) return;

        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (search && search.trim() !== '') {
          params.append("search", search.trim());
        }
        if (incluirInactivos) {
          params.append("incluir_inactivos", "true");
        }

        if (Array.isArray(tipo_producto) && tipo_producto.length > 0) {
          tipo_producto.forEach((tp) => params.append("tipo_producto", tp));
        }

        if (Array.isArray(origen) && origen.length > 0) {
          origen.forEach((o) => params.append("origen", o));
        }

        respuesta = await instanciaAxios.get(`/inventario/porsucursal/${id_sucursal}/paginado?${params.toString()}`);
      } else {
        // modo normal
        if (Array.isArray(tipo_producto) && tipo_producto.length > 0) {
          const params = new URLSearchParams();
          params.append("page", page);
          params.append("limit", limit);
          if (search && search.trim() !== '') {
            params.append("search", search.trim());
          }
          if (incluirInactivos) {
            params.append("incluir_inactivos", "true");
          }
          respuesta = await instanciaAxios.get(`/producto/portipo/${tipo_producto.join(",")}/paginado?${params.toString()}`);
        } else {
          const params = new URLSearchParams();
          params.append("page", page);
          params.append("limit", limit);
          if (search && search.trim() !== '') {
            params.append("search", search.trim());
          }
          if (incluirInactivos) {
            params.append("incluir_inactivos", "true");
          }
          respuesta = await instanciaAxios.get(`/producto/portipo/1,2,3/paginado?${params.toString()}`);
        }
      }

      if (respuesta.status === 200 && respuesta.data) {
        setProductos(respuesta.data.data || []);
        setTotalRows(respuesta.data.total || 0);
      } else {
        setProductos([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error al cargar los datos:", error);
      setProductos([]);
      setTotalRows(0);
    } finally {
      setCargando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo_tabla, id_sucursal, tipoProductoKey, origenKey, incluirInactivos]);

  // Carga inicial: primera página sin filtro
  useEffect(() => {
    fetchProductos(1, perPage, "");
  }, [fetchProductos, perPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchProductos(page, perPage, searchApplied);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchProductos(page, newPerPage, searchApplied);
  };

  const handleBuscar = () => {
    setSearchApplied(filtro);
    setCurrentPage(1);
    fetchProductos(1, perPage, filtro);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBuscar();
    }
  };

  const renderEstadoProducto = useCallback((row) => {
    const estaActivo = Number(row.id_estado) !== 2;
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
          estaActivo
            ? "bg-emerald-100 text-emerald-700"
            : "bg-red-100 text-red-700"
        }`}
      >
        {estaActivo ? "Activo" : "Inactivo"}
      </span>
    );
  }, []);

  const columns = useMemo(() => {
    if (tipo_tabla === "inventarioPorSucursal") {
      return [
        {
          name: "Cód.",
          selector: (row) => row.codigo_producto,
          sortable: true,
          width: "70px",
          format: (row) => row.codigo_producto
            ? String(row.codigo_producto).padStart(5, '0')
            : '—',
        },
        {
          name: "Referencia",
          selector: (row) => row.referencia,
          sortable: true,
        },
        {
          name: "Cod. Barras",
          selector: (row) => row.codigo_barra,
          sortable: true,
        },
        {
          name: "Descripción",
          selector: (row) => row.descripcion,
          sortable: true,
          cell: (row) => (
            <span className="flex items-center gap-1">
              {row.descripcion}
              {row.es_pesable && (
                <span title="Producto pesable" className="text-xs bg-blue-100 text-blue-700 px-1 rounded font-medium">
                  ⚖
                </span>
              )}
            </span>
          ),
        },
        {
          name: "Precio",
          selector: (row) => row.precio,
          sortable: true,
          format: (row) => formatoMoneda(row.precio || 0),
        },
        ...(incluirInactivos
          ? [
              {
                name: "Estado",
                selector: (row) => (Number(row.id_estado) === 2 ? "Inactivo" : "Activo"),
                sortable: true,
                cell: (row) => renderEstadoProducto(row),
              },
            ]
          : []),
      ];
    } else {
      return [
        {
          name: "Cód.",
          selector: (row) => row.codigo_producto,
          sortable: true,
          width: "70px",
          format: (row) => row.codigo_producto
            ? String(row.codigo_producto).padStart(5, '0')
            : '—',
        },
        {
          name: "Tipo",
          selector: (row) => row.tipo_producto,
          sortable: true,
        },
        {
          name: "Referencia",
          selector: (row) => row.referencia,
          sortable: true,
        },
        {
          name: "Cod. Barras",
          selector: (row) => row.codigo_barra,
          sortable: true,
        },
        {
          name: "Descripción",
          selector: (row) => row.descripcion,
          sortable: true,
          cell: (row) => (
            <span className="flex items-center gap-1">
              {row.descripcion}
              {row.es_pesable && (
                <span title="Producto pesable" className="text-xs bg-blue-100 text-blue-700 px-1 rounded font-medium">
                  ⚖
                </span>
              )}
            </span>
          ),
        },
        {
          name: "Marca",
          selector: (row) => row.marca,
          sortable: true,
        },
        ...(incluirInactivos
          ? [
              {
                name: "Estado",
                selector: (row) => (Number(row.id_estado) === 2 ? "Inactivo" : "Activo"),
                sortable: true,
                cell: (row) => renderEstadoProducto(row),
              },
            ]
          : []),
      ];
    }
  }, [tipo_tabla, incluirInactivos, formatoMoneda, renderEstadoProducto]);

  const handleRowClick = (producto) => {
    const productoSeleccionado = [producto.id_producto, producto.descripcion, producto.costo_promedio, producto.existencia];
    setProductoSeleccionado(productoSeleccionado);
  };

  if (id_sucursal == 0 && tipo_tabla == 'inventarioPorSucursal') {
    return (
      <div className="text-center text-gray-500 mt-5">
        Debe seleccionar una sucursal
      </div>
    );
  }

  return (
    <DataTable
      title={
        <>
          <h2 className="text-xl font-semibold text-center font-cascadia text-black">
            {tipo_tabla == 'inventarioPorSucursal' ? 'Inventario de la sucursal seleccionada' : 'Productos'}
          </h2>
          <p className="text-sm text-gray-500 text-center">
            {descripcion ? descripcion : null}
          </p>
        </>
      }
      columns={columns}
      data={productos}
      progressPending={cargando}
      progressComponent={<SpinnerCargando />}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      paginationDefaultPage={currentPage}
      paginationPerPage={perPage}
      paginationRowsPerPageOptions={[10, 20, 50]}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handlePerRowsChange}
      highlightOnHover
      onRowClicked={handleRowClick}
      dense
      noDataComponent={
        <div className="text-center text-gray-500 py-5">
          No se encontraron productos.
        </div>
      }
      subHeader
      subHeaderComponent={
        <div className="w-full flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Buscar por descripción, referencia o código de barras..."
              className="w-full border px-3 py-1 rounded-xl pr-10"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <button
            type="button"
            onClick={handleBuscar}
            className="flex items-center gap-1 px-4 py-1 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm"
          >
            <FaSearch />
            Buscar
          </button>
        </div>
      }
    />
  );
};

export default TablaProducto;
